/**
 * The motion scale as JavaScript reads it: the same `--transition-duration-*`
 * tokens `index.css` emits as `duration-fast` … `duration-slower`, for the
 * timers that back a CSS transition (an unmount after `transitionend`, a
 * fallback when no transition runs). Read from the document once, so a
 * retuned token moves the timer with the transition; the table is the value
 * where there is no document (a node test).
 */
export type DurationStep = "fast" | "base" | "slow" | "slower";

const FALLBACK_MS: Record<DurationStep, number> = {
  fast: 100,
  base: 200,
  slow: 300,
  slower: 500,
};

const read = new Map<DurationStep, number>();

export function durationToken(step: DurationStep): number {
  const cached = read.get(step);
  if (cached !== undefined) return cached;
  let ms = FALLBACK_MS[step];
  if (typeof document !== "undefined") {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue(`--transition-duration-${step}`)
      .trim();
    const parsed = raw.endsWith("ms")
      ? Number.parseFloat(raw)
      : raw.endsWith("s")
        ? Number.parseFloat(raw) * 1000
        : Number.NaN;
    if (Number.isFinite(parsed)) ms = parsed;
  }
  read.set(step, ms);
  return ms;
}
